import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  MapPin, 
  Clock, 
  MessageSquare, 
  Settings,
  Bell,
  Star,
  Users,
  Car,
  Calendar,
  Phone,
  Share2,
  Heart,
  Zap
} from 'lucide-react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Chip,
  Fade,
  Slide,
  Tooltip,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import { styled } from '@mui/material/styles';

const ActionCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
  }
}));

const ActionIcon = styled(Box)(({ theme, color }) => ({
  width: 56,
  height: 56,
  borderRadius: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${color} 0%, ${color}80 100%)`,
  boxShadow: `0 4px 20px ${color}40`,
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: `0 8px 30px ${color}60`,
  }
}));

const QuickActions = () => {
  const navigate = useNavigate();
  const [openSpeedDial, setOpenSpeedDial] = useState(false);

  const quickActions = [
    {
      title: 'Share a Ride',
      description: 'Create a new ride announcement',
      icon: Plus,
      color: '#6366f1',
      path: '/create-announcement',
      badge: 'Popular'
    },
    {
      title: 'Find Rides',
      description: 'Search for available rides',
      icon: Search,
      color: '#10b981',
      path: '/announcements',
      badge: 'New'
    },
    {
      title: 'My Connects',
      description: 'View your ride connections',
      icon: Users,
      color: '#f59e0b',
      path: '/connects'
    },
    {
      title: 'Ride History',
      description: 'View past rides and trips',
      icon: Clock,
      color: '#a855f7',
      path: '/ride-history'
    },
    {
      title: 'Messages',
      description: 'Chat with your ride partners',
      icon: MessageSquare,
      color: '#ec4899',
      path: '/chat',
      badge: '3 unread'
    },
    {
      title: 'Map View',
      description: 'See rides on the map',
      icon: MapPin,
      color: '#ef4444',
      path: '/map'
    }
  ];

  const speedDialActions = [
    {
      icon: <Plus size={20} />,
      name: 'Share Ride',
      action: () => navigate('/create-announcement')
    },
    {
      icon: <Search size={20} />,
      name: 'Find Rides',
      action: () => navigate('/announcements')
    },
    {
      icon: <MessageSquare size={20} />,
      name: 'Messages',
      action: () => navigate('/chat')
    },
    {
      icon: <MapPin size={20} />,
      name: 'Map View',
      action: () => navigate('/map')
    }
  ];

  const handleActionClick = (path) => {
    navigate(path);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Quick Actions
      </Typography>

      {/* Desktop Grid */}
      <Grid container spacing={3} sx={{ display: { xs: 'none', md: 'grid' } }}>
        {quickActions.map((action, index) => {
          const IconComponent = action.icon;
          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Slide direction="up" in timeout={600 + index * 100}>
                <ActionCard onClick={() => handleActionClick(action.path)}>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <ActionIcon color={action.color}>
                      <IconComponent size={28} color="white" />
                    </ActionIcon>
                    
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {action.title}
                    </Typography>
                    
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                      {action.description}
                    </Typography>
                    
                    {action.badge && (
                      <Chip 
                        label={action.badge}
                        size="small"
                        color={action.badge === 'Popular' ? 'primary' : 
                               action.badge === 'New' ? 'success' : 'warning'}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </CardContent>
                </ActionCard>
              </Slide>
            </Grid>
          );
        })}
      </Grid>

      {/* Mobile Speed Dial */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <SpeedDial
          ariaLabel="Quick actions"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            '& .MuiFab-primary': {
              width: 56,
              height: 56,
              background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
              boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 50%, #db2777 100%)',
                transform: 'scale(1.1)',
              }
            }
          }}
          icon={<SpeedDialIcon />}
          open={openSpeedDial}
          onOpen={() => setOpenSpeedDial(true)}
          onClose={() => setOpenSpeedDial(false)}
        >
          {speedDialActions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={() => {
                action.action();
                setOpenSpeedDial(false);
              }}
              sx={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 1)',
                  transform: 'scale(1.1)',
                }
              }}
            />
          ))}
        </SpeedDial>
      </Box>

      {/* Mobile Grid (Simplified) */}
      <Grid container spacing={2} sx={{ display: { xs: 'grid', md: 'none' } }}>
        {quickActions.slice(0, 4).map((action, index) => {
          const IconComponent = action.icon;
          return (
            <Grid item xs={6} key={index}>
              <Slide direction="up" in timeout={600 + index * 100}>
                <ActionCard onClick={() => handleActionClick(action.path)}>
                  <CardContent sx={{ textAlign: 'center', p: 2 }}>
                    <ActionIcon color={action.color}>
                      <IconComponent size={24} color="white" />
                    </ActionIcon>
                    
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {action.title}
                    </Typography>
                    
                    {action.badge && (
                      <Chip 
                        label={action.badge}
                        size="small"
                        color={action.badge === 'Popular' ? 'primary' : 
                               action.badge === 'New' ? 'success' : 'warning'}
                        sx={{ fontSize: '0.6rem', height: 20 }}
                      />
                    )}
                  </CardContent>
                </ActionCard>
              </Slide>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default QuickActions; 